import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../../utils/cmsAxios";
import SortableTable from "../CommonComponents/SortableTable";
// import { SlMagnifier } from "react-icons/sl";
import ConfirmModal from "../CommonComponents/ConfirmModal";
import PeriodPicker, { toKstIsoRange } from "../CommonComponents/PeriodPicker";
import { FiPlus } from "react-icons/fi";
import { Calendar } from "lucide-react";

const PAGE_SIZE = 10;

const CmsFaq = () => {
  const navigate = useNavigate();
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [openDelete, setOpenDelete] = useState(false);

  const [field, setField] = useState("all");      // all | title | content | writer
  const [keyword, setKeyword] = useState("");
  // const [post, setPost] = useState("all");
  const [category, setCategory] = useState("전체");
  // const [startDate, setStartDate] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  const [showJoinPicker, setShowJoinPicker] = useState(false);
  const [joinMode, setJoinMode] = useState("single"); // 'single' | 'range'
  const [joinSingle, setJoinSingle] = useState(null); // Date | null
  const [joinRange, setJoinRange] = useState({ from: null, to: null }); // {from,to}

  const [faqList, setFaqList] = useState([]);
  // const [selectedCategory, setSelectedCategory] = useState("All");
  // const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const getCurrentJoinParams = useCallback(() => {
    if (joinMode === "single" && joinSingle) {
        return toKstIsoRange(joinSingle, joinSingle);
    }
    if (joinMode === "range" && joinRange.from) {
        return toKstIsoRange(joinRange.from, joinRange.to || joinRange.from);
    }
    return {};
  }, [joinMode, joinSingle, joinRange]);

  const [applied, setApplied] = useState({
    field: "all",
    keyword: "",
    category: "전체",
    joinStart: "",
    joinEnd: "",
  });

  // 데이터 로드
  useEffect(() => {
    const fetchFaq = async () => {
      try {
        setLoading(true);
        const url = `${process.env.REACT_APP_FASTAPI_ADS_URL}/faq`;
        const { data } = await axios.get(url);
        setFaqList(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("FAQ 불러오기 실패:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFaq();
  }, []);

  //삭제 핸들러
  const handleDelete = useCallback(async (faq_id) => {
    const ids = faq_id ? [faq_id] : Array.from(selectedIds);
    if (ids.length === 0) return;

    // const ok = window.confirm(`선택한 ${ids.length}건을 삭제하시겠어요?`);
    // if (!ok) return;

    try {
      const url = `${process.env.REACT_APP_FASTAPI_ADS_URL}/faq/delete`;
      await Promise.all(
        ids.map((id) =>
          axios.post(url, { faq_id: id }, { headers: { "Content-Type": "application/json" } })
        )
      );

      const idSet = new Set(ids);
      setFaqList((prev) => prev.filter((f) => !idSet.has(f.faq_id)));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      setAlertMsg("삭제 되었습니다.");
      setOpenAlert(true);
    } catch (e) {
      console.error("FAQ 삭제 실패:", e);
      setAlertMsg("삭제 중 오류가 발생했습니다.");
      setOpenAlert(true);
    } finally {
      setOpenDelete(false); // 확인 모달 닫기
    }
  }, [selectedIds]);

  // 카테고리 목록
  const categories = useMemo(() => {
    const names = new Set(
      faqList
        .map((f) => f.category_name)
        .filter((v) => v !== undefined && v !== null && `${v}`.trim() !== "")
    );
    return ["전체", ...Array.from(names)];
  }, [faqList]);

  // 필터 + 검색
  const filteredFaqs = useMemo(() => {
    const { field: f, keyword: kw, category: cat, joinStart: js, joinEnd: je, } = applied;
    const q = (kw ?? "").toLowerCase();

    const hasRange = Boolean(js && je);
    const startMs = hasRange ? new Date(js).getTime() : null;
    const endMs   = hasRange ? new Date(je).getTime() : null;

    return faqList.filter((item) => {
      if (cat !== "전체" && (item.category_name ?? "") !== cat) return false;

      // 기간
      if (hasRange) {
        const t = new Date(item.created_at).getTime();
        if (!Number.isFinite(t) || t < startMs || t > endMs) return false;
      }

      // 키워드
      if (!q) return true;
      const title = String(item.question ?? "").toLowerCase();
      const content = String(item.answer ?? "").toLowerCase();
      const writer = String(item.create_user ?? item.writer ?? "").toLowerCase();
      if (f === "title") return title.includes(q);
      if (f === "content") return content.includes(q);
      if (f === "writer") return writer.includes(q);
      return [title, content, writer].some((v) => v.includes(q));
    });
  }, [faqList, applied]);

  const handleSearch = () => {
    const cur = getCurrentJoinParams();
    setApplied({ field, keyword, category, joinStart: cur.join_start || "", joinEnd: cur.join_end || "", });
    setPage(1);
  };

  const isDirty = useMemo(() => {
    const cur = getCurrentJoinParams();
    const curStart = cur.join_start || "";
    const curEnd = cur.join_end || "";
    return !(
      field  === applied.field  &&
      (keyword || "") === (applied.keyword || "") &&
      category === applied.category &&
      (curStart || "") === (applied.joinStart || "") &&
      (curEnd || "") === (applied.joinEnd || "")
    );
  }, [field, keyword, category, applied, getCurrentJoinParams]);


  const handleReset = () => {
    setField("all");
    setKeyword("");
    setCategory("전체");
    setShowJoinPicker(false);
    setJoinMode("single"); 
    setJoinSingle(null);
    setJoinRange({ from: null, to: null });

    setApplied({
      field: "all", keyword: "", category: "전체",
      joinStart: "",
      joinEnd: "",
    });
    setPage(1);
  };

  // 페이지네이션 계산
  const totalPages = Math.max(1, Math.ceil(filteredFaqs.length / PAGE_SIZE));
  const pagedFaqs = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredFaqs.slice(start, start + PAGE_SIZE);
  }, [filteredFaqs, page]);

  // 페이지 이동
  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

  // 카테고리/검색 변경 시 1페이지로
  useEffect(() => {
    setPage(1);
  // }, [selectedCategory, searchText]);
  }, [applied]);

  // 글쓰기 이동
  const handleCreateFaq = () => {
    navigate("/cms/faq/create");
  };

  // 수정 이동
  const handleEdit = (faq) => {
    navigate("/cms/faq/create", { state: { faq } });
  };



  // 날짜 포맷
  // const formatDate = (d) => {
  //   if (!d) return "-";
  //   const dt = typeof d === "string" ? new Date(d) : d;
  //   if (Number.isNaN(dt.getTime())) return "-";
  //   const y = dt.getFullYear();
  //   const m = String(dt.getMonth() + 1).padStart(2, "0");
  //   const day = String(dt.getDate()).padStart(2, "0");
  //   return `${y}-${m}-${day}`;
  // };
  
  const withDateTooltip = useCallback((v) => {
      if (!v) return "-";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "-";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      const ss = String(d.getSeconds()).padStart(2, "0");
      const tip = `${y}.${m}.${dd} ${hh}:${mm}:${ss}`;
      return <time dateTime={d.toISOString()} title={tip}>{`${y}-${m}-${dd}`}</time>;
    }, []);
  
  // const totalCount = filteredFaqs.length;
  // const post = (v) => (v === true || v === 1 || v === "Y" || v === "y") ? "Y" : "N";
  const post = (v) => "Y";

  const isSelectable = useCallback((row) => post(row?.is_active) !== "Y", []);

  const selectableTotal = useMemo(
    () => pagedFaqs.filter(isSelectable).length,
    [pagedFaqs, isSelectable]
  );

  const allChecked = useMemo(
    () => selectableTotal > 0 && selectedIds.size === selectableTotal,
    [selectableTotal, selectedIds]
  );

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const shouldUncheck = prev.size === selectableTotal;
      if (shouldUncheck) return new Set();
      return new Set(pagedFaqs.filter(isSelectable).map(r => r.faq_id));
    });
  }, [pagedFaqs, selectableTotal, isSelectable]);

  const toggleOne = useCallback((id) => {
    const row = pagedFaqs.find(r => r.faq_id === id);
    if (!row || !isSelectable(row)) return; // Y면 무시
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, [pagedFaqs, isSelectable]);

  const columns = useMemo(() => ([
    {
      key: "_select",
      header: "",
      sortable: false,
      headerRender: () => (
        <input
          type="checkbox"
          checked={allChecked}
          onChange={toggleAll}
          disabled={selectableTotal === 0}
          className="w-4 h-4"
          aria-label="전체 선택"
        />
      ),
      render: (_v, row) => (
        <input
          type="checkbox"
          checked={selectedIds.has(row.faq_id)}
          disabled={!isSelectable(row)}
          onChange={(e) => { e.stopPropagation(); toggleOne(row.faq_id); }}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4"
          aria-label="선택"
        />
      ),
    },
    // { key: "_rownum", header: "번호", type: "number" },
    { key: "is_active", header: "노출여부", type: "string", align: "center",
      sortBy: (row) => (post(row.is_active) === "Y" ? 1 : 0),
      render: (v, row) => post(row.is_active),
    },
    { key: "category_name", header: "카테고리 종류", type: "string", render: (v) => v || "미분류", },
    { key: "question", header: "제목", type: "string", render: (v, row) => (
      <div  className="font-semibold text-left line-clamp-1"  title={row.question} >
        {row.question}
      </div>),
    },
    { key: "create_user", header: "등록(수정)자", type: "string", align: "center", render: (_v, row) => row.create_user ?? row.writer ?? "admin", },
    { key: "created_at", header: "등록(수정)일", type: "date", align: "center", render: withDateTooltip },
    ]), [allChecked, selectableTotal, selectedIds, toggleAll, toggleOne, isSelectable, withDateTooltip]);

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="mx-auto max-w-[1280px] bg-white rounded-2xl shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_40px_80px_-24px_rgba(0,0,0,0.12)] overflow-hidden">
        <div className="flex">
          {/* Left Nav */}


          {/* Right content */}
          <div className="flex-1 min-w-0 bg-neutral-50/70">

            <main className="pt-12 flex flex-col gap-4 p-4 overflow-x-hidden w-full">
              <section className="p-4">
                <div className="flex justify-between items-center pb-4">
                  <h2 className="text-xl font-semibold mb-3">FAQ</h2>

                </div>

                {/* 상단: 검색 + 등록 버튼 */}
                {/* <div className="flex items-center justify-between mb-4">
                  <div className="relative">
                    <input
                      type="text"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder="검색어를 입력하세요 (카테고리, 제목, 내용)"
                      className="w-[25vw] h-10 px-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none text-xl">
                      <SlMagnifier />
                    </span>
                  </div>

                  <button
                    onClick={handleCreateFaq}
                    className="h-10 px-4 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                  >
                    새 FAQ 등록 +
                  </button>
                </div> */}
                <div className="rounded-lg bg-white p-3">
                  <div className="flex flex-wrap items-end gap-6">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-neutral-500">검색</span>
                      <div className="flex items-center gap-2">
                        <select
                          value={field}
                          onChange={(e) => setField(e.target.value)}
                          className="h-9 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                        >
                          <option value="all">전체</option>
                          <option value="title">제목</option>
                          {/* <option value="content">내용</option> */}
                          <option value="writer">등록(수정)자</option>
                        </select>
                        <input
                          value={keyword}
                          onChange={(e) => setKeyword(e.target.value)}
                          type="text"
                          placeholder="검색어를 입력하세요"
                          className="h-9 w-52 rounded-md border border-neutral-300 bg-white px-3 text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-violet-200"
                        />
                      </div>
                    </div>
                    {/* <div className="flex flex-col gap-1">
                      <span className="text-sm text-neutral-500">노출 여부</span>
                      <select
                        value={post}
                        onChange={(e) => setPost(e.target.value)}
                        className="h-9 w-24 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                      >
                        <option value="all">전체</option>
                        <option value="Y">Y</option>
                        <option value="N">N</option>
                      </select>
                    </div> */}
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-neutral-500">카테고리</span>
                      <select
                        value={category}
                        onChange={e => setCategory(e.target.value)}
                        className="h-9 w-44 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                      >
                        {categories.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-neutral-500">등록(수정)일</span>
                      <button
                        type="button"
                        onClick={() => setShowJoinPicker(true)}
                        className="h-9 w-[230px] inline-flex items-center justify-between gap-2
                                rounded-md border border-neutral-300 bg-white px-2 text-sm whitespace-nowrap"
                      >
                        <span className="truncate">
                        {(() => {
                          const cur = getCurrentJoinParams();
                          if (cur.join_start && cur.join_end) {
                            const s = new Date(cur.join_start);
                            const e = new Date(cur.join_end);
                            const fmt = (d) =>
                              `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                            return `${fmt(s)} ~ ${fmt(e)}`;
                          }
                          return "-";
                        })()}
                        </span>
                        <Calendar />
                      </button>

                      {/* 달력: 열렸을 때만 렌더 */}
                      {showJoinPicker && (
                        <>
                          <div
                            className="fixed inset-0 bg-black/30 z-[40]"
                            onClick={() => setShowJoinPicker(false)}
                          />
                          <div className="absolute z-[50] mt-2 rounded-lg border bg-white shadow-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <select
                                value={joinMode}
                                onChange={(e) => setJoinMode(e.target.value)}
                                className="h-8 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                              >
                                <option value="single">하루</option>
                                <option value="range">기간</option>
                              </select>
                            </div>
                            
                            <PeriodPicker
                              mode={joinMode}
                              value={joinMode === "single" ? joinSingle : joinRange}
                              onChange={(val) => {
                                if (joinMode === "single") {setJoinSingle(val || null);}
                                else {setJoinRange(val ?? { from: null, to: null });}
                              }}
                              onConfirm={() => setShowJoinPicker(false)}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={handleReset}
                      className="h-9 px-3 rounded-md bg-black/10 border border-neutral-300 text-neutral-700 hover:bg-black/50"
                    >
                      초기화
                    </button>
                    <button
                      type="button"
                      onClick={handleSearch}
                      disabled={!isDirty}
                      className={`h-9 px-9 rounded-md text-white shadow-sm  ${isDirty ? "bg-[#7864F9] hover:bg-violet-600" : "bg-violet-400/40 cursor-not-allowed"}`}
                    >
                      검색
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2 py-3">
                  <div className="text-m text-neutral-600">
                    총 <span className="text-violet-500">{filteredFaqs.length} 건</span>
                  </div>
                  <div className="ml-auto flex gap-2">
                    <button
                      type="button"
                      onClick={handleCreateFaq}
                      className="rounded-lg text-sm px-3 py-1.5 bg-[#7864F9]/10 text-[#7864F9] inline-flex items-center"
                    >
                      <FiPlus className="mr-1" />
                      등록
                    </button>
                    <span
                      aria-hidden="true"
                      className="mx-2 h-7 w-[2px] mt-[5px] bg-[#D9D9D9]"
                    />
                    <button
                      type="button"
                      onClick={() => setOpenDelete(true)}
                      disabled={selectedIds.size === 0}
                      className={`rounded-lg border px-3 py-1.5 text-sm ${selectedIds.size === 0 ? "bg-black/10 text-black/50 cursor-not-allowed" : "bg-black/10 hover:bg-black/20 text-black"}`}
                    >
                      삭제
                    </button>
                  </div>
                </div>

                {/* 카테고리 필터 */}
                {/* <div className="flex flex-wrap gap-2 pb-4">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-3 py-1.5 rounded-full border text-sm transition ${selectedCategory === cat
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200"
                        }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div> */}

                {/* 테이블 */}
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white p-4">
                  {loading ? (
                        <div className="px-4 py-10 text-center text-gray-500">불러오는 중…</div>
                      ) : pagedFaqs.length === 0 ? (
                        <div className="px-4 py-10 text-center text-gray-500">FAQ가 없습니다.</div>
                        
                      ) : (               
                      <SortableTable
                        data={pagedFaqs}
                        columns={columns}
                        defaultSort={{ key: "created_at", dir: "desc" }}
                        rowKey="faq_id"
                        onRowClick={(row) => handleEdit(row)}            // 행 클릭 시 수정 이동
                        rowClassName={() => "border-t border-gray-100"}
                        // tableClassName="w-full table-fixed"
                        tableClassName="min-w-full border-separate border-spacing-0"
                        headerCellClassName="bg-neutral-50 text-black/50 first:rounded-tl-xl last:rounded-tr-xl text-sm"
                        // headClassName="bg-gray-50 text-gray-600 text-sm"
                        bodyClassName="text-sm text-black/50"
                        columnWidths={["4ch","10ch","16ch","auto","13ch","13ch"]} // 번호/노출/카테고리/제목/작성자/등록일
                    />
                      )}
                </div>

                {/* 페이지네이션 */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-4">
                    <button
                      className="px-2 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-50"
                      onClick={goPrev}
                      disabled={page === 1}
                    >
                      이전
                    </button>

                    {Array.from({ length: totalPages }).map((_, i) => (
                      <button
                        key={i}
                        className={`w-8 h-8 rounded-lg text-sm border transition ${page === i + 1
                          ? "bg-[#7864F9] text-white"
                          : "border-gray-200 hover:bg-gray-100"
                          }`}
                        onClick={() => setPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    ))}

                    <button
                      className="px-2 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-50"
                      onClick={goNext}
                      disabled={page === totalPages}
                    >
                      다음
                    </button>
                  </div>
                )}
              </section>
              {/* 삭제 확인 모달 */}
              <ConfirmModal
                open={openDelete}
                title="삭제"
                message={
                  <>
                  <div className="mb-2">
                    {selectedIds.size}개가 선택되었습니다.
                  </div>
                  <div>해당 게시물을 삭제하시겠습니까?</div>
                  </>
                }
                onClose={() => setOpenDelete(false)}
                buttons={[
                  { label: "삭제", intent: "primary", autoFocus: true, onClick: () => handleDelete() },
                  { label: "취소", onClick: () => setOpenDelete(false) },                  
                ]}
              />

              {/* 결과 알림 모달 */}
              <ConfirmModal
                open={openAlert}
                title=""
                message={alertMsg || "삭제 되었습니다."}
                onClose={() => setOpenAlert(false)}
                buttons={[
                  { label: "확인", intent: "primary", autoFocus: true, onClick: () => setOpenAlert(false) },
                ]}
              />
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CmsFaq;
